export type User = {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phoneNumber: string | null;
  taxId: string | null;
};

export type AccountProfile = User & { version: number };

export type Business = {
  id: string;
  displayName: string;
  legalName: string | null;
  taxId: string | null;
  countryCode: string;
  timezone: string;
  status: string;
  version: number;
  role: string;
  createdAt: string;
};

export type SessionData = { user: User; businesses: Business[] };

export type ActiveSession = {
  id: string;
  createdAt: string;
  lastSeenAt: string;
  expiresAt: string;
  current: boolean;
};

export type Employee = {
  id: string;
  staffNumber: string;
  givenName: string;
  familyName: string;
  otherNames: string | null;
  employmentStartDate: string;
  employmentEndDate: string | null;
  employmentStatus: "active" | "on_leave" | "ended" | "archived";
  employmentType: string;
  residenceStateCode: string;
  residenceEffectiveFrom: string;
  version: number;
};

export type PayrollInput = {
  basicPayKobo: number;
  housingAllowanceKobo: number;
  transportAllowanceKobo: number;
  bonusKobo: number;
  otherCashEmolumentsKobo: number;
  pensionContributionKobo: number;
  pensionDecision: "accepted" | "pending" | "rejected";
  pensionEvidenceStatus: "declared" | "provided" | "reviewed" | "rejected";
  annualRentPaidKobo: number | null;
  rentDeclarationConfirmed: boolean;
  openingThroughMonth: number;
  openingCashEmolumentsKobo: number;
  openingTaxableBenefitsKobo: number;
  openingExemptIncomeKobo: number;
  openingEligibleDeductionsKobo: number;
  openingPayeDeductedKobo: number;
  openingSourceConfirmed: boolean;
};

export type PayrollRun = {
  id: string;
  periodYear: number;
  periodMonth: number;
  runType: "ordinary" | "correction";
  status: "draft" | "needs_review" | "ready_for_approval" | "approved" | "superseded" | "cancelled";
  revision: number;
  employeeCount: number;
  totalGrossKobo: number;
  totalPayeKobo: number;
  warningCount: number;
  ruleVersion: string | null;
  ruleChecksum: string | null;
  engineVersion: string | null;
  approvedAt: string | null;
  updatedAt: string;
};

export type PayrollItem = {
  id: string;
  employeeId: string;
  staffNumber: string;
  employeeName: string;
  residenceStateCode: string;
  calculationStatus: string;
  currentPeriodPayeKobo: number;
  warningCount: number;
  version: number;
  input: PayrollInput;
  calculation: { issues?: Array<{ severity: string; code: string; message: string }>; trace?: Array<{ label?: string; valueKobo?: number; ruleReference?: string }> } | null;
};

export type ReportRevision = {
  id: string;
  payrollRunId: string;
  revision: number;
  status: string;
  snapshotChecksum: string;
  generatedAt: string;
  periodYear: number;
  periodMonth: number;
  totalPayeKobo: number;
};

export type AppNotification = {
  id: string;
  eventType: string;
  title: string;
  body: string;
  resourceType: string | null;
  resourceId: string | null;
  readAt: string | null;
  createdAt: string;
};

export type Overview = {
  employeeCount: number;
  recentRuns: PayrollRun[];
  reportCount: number;
  unreadNotifications: number;
  productionApprovalBlocked: boolean;
  blockReason: string;
};

export type PersonalTaxDecision = "accepted" | "pending" | "rejected";
export type PersonalTaxEvidenceStatus = "not_required" | "declared" | "provided" | "reviewed" | "rejected";

export type PersonalTaxComponent = {
  code: string;
  label: string;
  amountKobo: number;
  decision: PersonalTaxDecision;
  evidenceStatus: PersonalTaxEvidenceStatus;
};

export type PersonalIncomeComponent = PersonalTaxComponent & {
  category: "employment" | "business" | "professional" | "investment" | "rental" | "other";
};

export type PersonalTaxCredit = PersonalTaxComponent & {
  creditType: "paye" | "withholding_tax" | "advance_payment" | "other";
};

export type PersonalTaxInput = {
  version: number;
  residencyStatus: "resident_ordinary" | "non_resident";
  principalResidenceStateCode: string;
  specialFederalCategory: boolean;
  hasForeignIncome: boolean;
  incomeSources: PersonalIncomeComponent[];
  exemptIncome: PersonalTaxComponent[];
  eligibleDeductions: PersonalTaxComponent[];
  annualRentPaidKobo: number | null;
  rentDeclarationConfirmed: boolean;
  taxCredits: PersonalTaxCredit[];
};

export type PersonalTaxCalculation = {
  status: "blocked" | "needs_review" | "calculated";
  engineVersion: string;
  inputChecksum: string;
  resultChecksum: string;
  issues: Array<{ code: string; severity: "blocking" | "warning"; message: string; remediation: string }>;
  values: null | {
    grossIncomeKobo: number;
    exemptIncomeKobo: number;
    totalIncomeKobo: number;
    eligibleDeductionsBeforeRentKobo: number;
    rentReliefKobo: number;
    chargeableIncomeKobo: number;
    annualTaxLiabilityKobo: number;
    taxCreditsKobo: number;
    balancePayableKobo: number;
    overpaymentKobo: number;
    effectiveTaxRateBasisPoints: number;
  };
  trace: Array<{
    sequence: number;
    code: string;
    label: string;
    resultKobo: number;
    rateBasisPoints: number | null;
    ruleReferences: string[];
  }>;
  rule: { version: string; checksum: string; status: string };
};

export type PersonalTaxCase = {
  id: string;
  assessmentYear: number;
  revision: number;
  status: "draft" | "blocked" | "needs_review" | "calculated" | "confirmed" | "superseded" | "archived";
  residencyStatus: "resident_ordinary" | "non_resident";
  principalResidenceStateCode: string;
  specialFederalCategory: boolean;
  hasForeignIncome: boolean;
  ruleVersion: string | null;
  ruleChecksum: string | null;
  engineVersion: string | null;
  grossIncomeKobo: number;
  chargeableIncomeKobo: number;
  annualTaxLiabilityKobo: number;
  taxCreditsKobo: number;
  balancePayableKobo: number;
  overpaymentKobo: number;
  warningCount: number;
  version: number;
  createdAt: string;
  updatedAt: string;
  input: PersonalTaxInput;
  calculation: PersonalTaxCalculation | null;
};

export type PersonalTaxReport = {
  id: string;
  personalTaxCaseId: string;
  revision: number;
  status: "available" | "superseded";
  snapshotChecksum: string;
  generatedAt: string;
  assessmentYear: number;
  annualTaxLiabilityKobo: number;
  balancePayableKobo: number;
  overpaymentKobo: number;
};

export type DocumentRecord = {
  id: string;
  category: "bank_statement" | "payslip" | "receipt" | "filing_proof" | "other";
  dataClassification: "synthetic" | "customer";
  status: "created" | "uploading" | "quarantined" | "validating" | "scan_pending" | "accepted" | "conversion_pending" | "extracting" | "needs_review" | "confirmed" | "archived" | "upload_failed" | "rejected" | "scan_failed" | "conversion_failed" | "extraction_failed" | "deletion_pending" | "deleted";
  objectStore: "quarantine" | "documents" | "deleted";
  originalFilename: string;
  filenameExtension: string;
  declaredMimeType: string;
  detectedMimeType: string | null;
  sizeBytes: number | null;
  pageCount: number | null;
  sha256: string | null;
  rejectionCode: string | null;
  scanStatus: string;
  retentionDeleteAfter: string | null;
  deletedAt: string | null;
  version: number;
  createdAt: string;
  updatedAt: string;
};

export type ExtractionRecord = {
  id: string;
  documentId: string;
  schemaType: "bank_statement" | "payslip" | "receipt";
  schemaVersion: string;
  promptVersion: string;
  revision: number;
  status: "queued" | "converting" | "extracting" | "needs_review" | "confirmed" | "rejected" | "failed";
  sourceChecksum: string;
  provider: string | null;
  model: string | null;
  modelVersion: string | null;
  requestId: string | null;
  attemptCount: number;
  failureCode: string | null;
  confirmedAt: string | null;
  originalFilename: string;
  category: string;
  dataClassification: string;
  createdAt: string;
  updatedAt: string;
};

export type ExtractionField = {
  id: string;
  fieldKey: string;
  label: string;
  valueType: "string" | "date" | "money_kobo" | "integer";
  confidenceBasisPoints: number;
  evidencePage: number | null;
  evidenceLocation: string | null;
  evidenceExcerpt: string | null;
  reviewDecision: "pending" | "accepted" | "corrected" | "rejected";
  reviewedAt: string | null;
  version: number;
  suggestedValue: string | number | null;
  confirmedValue: string | number | null;
};

export type ExtractionDetail = { extraction: ExtractionRecord; fields: ExtractionField[] };

export type TaxAuthority = {
  code: "LIRS" | "FCTIRS";
  stateCode: "LA" | "FC";
  name: string;
  jurisdictionType: "state" | "fct" | "federal";
  primaryUrl: string;
  registrationUrl: string;
  filingUrl: string;
  paymentUrl: string;
  guidanceUrl: string;
  supportUrl: string;
  requirements: string[];
  sourceUrl: string;
  verifiedAt: string;
  status: string;
  version: number;
};

export type FilingRecord = {
  id: string;
  personalTaxCaseId: string;
  authorityCode: string;
  authorityName: string;
  status: "prepared" | "submitted_by_user" | "paid_by_user";
  externalReference: string | null;
  proofDocumentId: string | null;
  userAttestedAt: string;
  updatedAt: string;
  assessmentYear: number;
};

export type BillingPlan = {
  id: "personal" | "sme" | "corporate" | "enterprise" | "consultant";
  name: string;
  description: string;
  annualPriceKobo: number | null;
  vatBasisPoints: number;
  currency: "NGN";
  employeeLimit: number | null;
  userLimit: number | null;
  checkoutEnabled: number;
  displayOrder: number;
  version: number;
  vatAmountKobo: number | null;
  totalAmountKobo: number | null;
  entitlements: Record<string, { mode: string; limit: number | null }>;
};

export type BillingSubscription = {
  id: string;
  planId: string | null;
  planName: string | null;
  provider: "pilot" | "paystack";
  status: string;
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  renewsAt: string | null;
  graceEndsAt: string | null;
  employeeLimit: number | null;
  userLimit: number | null;
  displayOrder: number | null;
};

export type BillingTransaction = {
  id: string;
  planId: string | null;
  reference: string;
  kind: string;
  status: string;
  baseAmountKobo: number;
  vatAmountKobo: number;
  totalAmountKobo: number;
  currency: string;
  paidAt: string | null;
  createdAt: string;
};

export type BillingSummary = {
  subscription: BillingSubscription | null;
  accessAllowed: boolean;
  usage: { activeEmployees: number; activeUsers: number };
  transactions: BillingTransaction[];
};

type ErrorPayload = {
  error?: {
    code?: string;
    message?: string;
    fields?: Array<{ field: string; message: string }>;
  };
  requestId?: string;
};

export class ApiClientError extends Error {
  readonly code: string;
  readonly requestId?: string;
  readonly fields: Record<string, string>;

  constructor(payload: ErrorPayload, fallback: string) {
    super(payload.error?.message ?? fallback);
    this.name = "ApiClientError";
    this.code = payload.error?.code ?? "request_failed";
    this.requestId = payload.requestId;
    this.fields = Object.fromEntries((payload.error?.fields ?? []).map((item) => [item.field, item.message]));
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(`/api/v1${path}`, {
    ...init,
    credentials: "include",
    headers: {
      ...(init.body ? { "Content-Type": "application/json" } : {}),
      ...init.headers
    }
  });
  const payload = await response.json() as { data?: T } & ErrorPayload;
  if (!response.ok || payload.data === undefined) {
    throw new ApiClientError(payload, "The request could not be completed.");
  }
  return payload.data;
}

async function uploadDocumentContent(documentId: string, file: File): Promise<{ document: DocumentRecord }> {
  const response = await fetch(`/api/v1/documents/${documentId}/content`, {
    method: "PUT",
    credentials: "include",
    headers: { "Content-Type": file.type || "application/octet-stream" },
    body: file
  });
  const payload = await response.json() as { data?: { document: DocumentRecord } } & ErrorPayload;
  if (!response.ok || payload.data === undefined) {
    throw new ApiClientError(payload, "The document could not be uploaded.");
  }
  return payload.data;
}

export const api = {
  session: () => request<SessionData>("/auth/session"),
  register: (input: { firstName: string; lastName: string; email: string; password: string; inviteToken?: string }) =>
    request<{ user: User; verificationRequired: boolean; developmentVerificationToken?: string }>("/auth/register", {
      method: "POST",
      body: JSON.stringify(input)
    }),
  verifyEmail: (token: string) => request<{ verified: boolean; email: string }>("/auth/verify-email", {
    method: "POST",
    body: JSON.stringify({ token })
  }),
  signIn: (email: string, password: string) => request<{ user: User; expiresAt: string }>("/auth/sign-in", {
    method: "POST",
    body: JSON.stringify({ email, password })
  }),
  signOut: () => request<{ signedOut: boolean }>("/auth/sign-out", {
    method: "POST",
    body: JSON.stringify({})
  }),
  forgotPassword: (email: string) => request<{
    accepted: boolean;
    message: string;
    developmentResetToken?: string;
  }>("/auth/password/forgot", {
    method: "POST",
    body: JSON.stringify({ email })
  }),
  resetPassword: (token: string, password: string) => request<{ reset: boolean }>("/auth/password/reset", {
    method: "POST",
    body: JSON.stringify({ token, password })
  }),
  profile: () => request<{ profile: AccountProfile }>("/auth/profile"),
  updateProfile: (input: { firstName: string; lastName: string; phoneNumber: string | null; taxId: string | null }) =>
    request<{ profile: AccountProfile }>("/auth/profile", {
      method: "PATCH",
      body: JSON.stringify(input)
    }),
  changePassword: (currentPassword: string, newPassword: string) =>
    request<{ changed: boolean; otherSessionsRevoked: boolean }>("/auth/password/change", {
      method: "POST",
      body: JSON.stringify({ currentPassword, newPassword })
    }),
  sessions: () => request<{ sessions: ActiveSession[] }>("/auth/sessions"),
  revokeSession: (sessionId: string) => request<{ revoked: boolean }>(`/auth/sessions/${sessionId}`, {
    method: "DELETE",
    body: JSON.stringify({})
  }),
  revokeAllSessions: () => request<{ revoked: boolean }>("/auth/sessions/revoke-all", {
    method: "POST",
    body: JSON.stringify({})
  }),
  createBusiness: (input: { displayName: string; legalName?: string }) =>
    request<{ business: Business }>("/businesses", {
      method: "POST",
      headers: { "Idempotency-Key": crypto.randomUUID() },
      body: JSON.stringify(input)
    }),
  updateBusiness: (businessId: string, input: { version: number; displayName: string; legalName: string | null; taxId: string | null }) =>
    request<{ business: Business }>(`/businesses/${businessId}`, {
      method: "PATCH",
      body: JSON.stringify(input)
    }),
  overview: (businessId: string) => request<Overview>(`/payroll/businesses/${businessId}/overview`),
  employees: (businessId: string) => request<{ employees: Employee[] }>(`/payroll/businesses/${businessId}/employees`),
  createEmployee: (businessId: string, input: {
    staffNumber: string;
    givenName: string;
    familyName: string;
    otherNames?: string;
    employmentStartDate: string;
    employmentEndDate?: string | null;
    principalResidenceStateCode: string;
    residenceEffectiveFrom: string;
  }) => request<{ employee: Employee }>(`/payroll/businesses/${businessId}/employees`, {
    method: "POST",
    body: JSON.stringify(input)
  }),
  updateEmployee: (businessId: string, employeeId: string, input: {
    version: number;
    givenName: string;
    familyName: string;
    otherNames: string | null;
    employmentEndDate: string | null;
    employmentStatus: Employee["employmentStatus"];
    principalResidenceStateCode: string;
    residenceEffectiveFrom: string;
  }) => request<{ updated: boolean; employeeId: string; version: number }>(`/payroll/businesses/${businessId}/employees/${employeeId}`, {
    method: "PATCH",
    body: JSON.stringify(input)
  }),
  previewEmployeeImport: (businessId: string, csv: string) => request<{
    batchId: string;
    sourceChecksum: string;
    valid: boolean;
    rows: Array<{
      rowNumber: number;
      staffNumber: string;
      givenName: string;
      familyName: string;
      employmentStartDate: string;
      employmentEndDate: string | null;
      principalResidenceStateCode: string;
      errors: string[];
    }>;
    errors: string[];
  }>(`/payroll/businesses/${businessId}/employees/import/preview`, {
    method: "POST",
    body: JSON.stringify({ csv })
  }),
  commitEmployeeImport: (businessId: string, batchId: string, sourceChecksum: string) => request<{ committed: boolean; employeeCount: number }>(`/payroll/businesses/${businessId}/employees/import/commit`, {
    method: "POST",
    body: JSON.stringify({ batchId, sourceChecksum })
  }),
  runs: (businessId: string) => request<{ runs: PayrollRun[] }>(`/payroll/businesses/${businessId}/runs`),
  createRun: (businessId: string, periodYear: number, periodMonth: number) => request<{ run: PayrollRun }>(`/payroll/businesses/${businessId}/runs`, {
    method: "POST",
    body: JSON.stringify({ periodYear, periodMonth })
  }),
  run: (businessId: string, runId: string) => request<{ run: PayrollRun; items: PayrollItem[] }>(`/payroll/businesses/${businessId}/runs/${runId}`),
  savePayrollItem: (businessId: string, runId: string, itemId: string, input: PayrollInput) => request<{ saved: boolean }>(`/payroll/businesses/${businessId}/runs/${runId}/items/${itemId}`, {
    method: "PATCH",
    body: JSON.stringify(input)
  }),
  calculateRun: (businessId: string, runId: string) => request<{ run: PayrollRun; warnings: Array<{ itemId: string; staffNumber: string; code: string; message: string }> }>(`/payroll/businesses/${businessId}/runs/${runId}/calculate`, {
    method: "POST",
    body: JSON.stringify({})
  }),
  reports: (businessId: string) => request<{ reports: ReportRevision[] }>(`/payroll/businesses/${businessId}/reports`),
  notifications: (businessId: string) => request<{ notifications: AppNotification[] }>(`/payroll/businesses/${businessId}/notifications`),
  markNotificationRead: (businessId: string, notificationId: string) => request<{ read: boolean }>(`/payroll/businesses/${businessId}/notifications/${notificationId}/read`, {
    method: "PATCH",
    body: JSON.stringify({})
  }),
  personalTaxCases: () => request<{ cases: PersonalTaxCase[] }>("/personal-tax/cases"),
  createPersonalTaxCase: (assessmentYear: number, principalResidenceStateCode: string) =>
    request<{ case: PersonalTaxCase }>("/personal-tax/cases", {
      method: "POST",
      body: JSON.stringify({ assessmentYear, principalResidenceStateCode })
    }),
  personalTaxCase: (caseId: string) => request<{ case: PersonalTaxCase }>(`/personal-tax/cases/${caseId}`),
  savePersonalTaxCase: (caseId: string, input: PersonalTaxInput) =>
    request<{ saved: boolean; version: number }>(`/personal-tax/cases/${caseId}`, {
      method: "PATCH",
      body: JSON.stringify(input)
    }),
  calculatePersonalTaxCase: (caseId: string) =>
    request<{ case: PersonalTaxCase }>(`/personal-tax/cases/${caseId}/calculate`, {
      method: "POST",
      body: JSON.stringify({})
    }),
  confirmPersonalTaxCase: (caseId: string) =>
    request<{ confirmed: boolean; reportId: string; snapshotChecksum: string }>(`/personal-tax/cases/${caseId}/confirm`, {
      method: "POST",
      body: JSON.stringify({
        acknowledgeInputsAreComplete: true,
        acknowledgeNotFiledOrPaid: true,
        acknowledgeProfessionalReviewBoundary: true
      })
    }),
  personalTaxReports: () => request<{ reports: PersonalTaxReport[] }>("/personal-tax/reports"),
  personalTaxReportUrl: (reportId: string, format: "csv" | "pdf") => `/api/v1/personal-tax/reports/${reportId}/${format}`,
  documents: () => request<{ documents: DocumentRecord[] }>("/documents"),
  createDocumentIntent: (input: {
    originalFilename: string;
    category: DocumentRecord["category"];
    declaredMimeType: "application/pdf" | "image/png" | "image/jpeg" | "text/csv";
    sizeBytes: number;
    dataClassification: "synthetic" | "customer";
  }) => request<{ documentId: string; uploadUrl: string; expiresAt: string; maximumBytes: number }>("/documents/intents", {
    method: "POST",
    body: JSON.stringify(input)
  }),
  uploadDocumentContent,
  deleteDocument: (documentId: string) => request<{ deleted: boolean }>(`/documents/${documentId}`, {
    method: "DELETE",
    body: JSON.stringify({})
  }),
  documentContentUrl: (documentId: string) => `/api/v1/documents/${documentId}/content`,
  extractions: () => request<{ extractions: ExtractionRecord[] }>("/extractions"),
  startExtraction: (documentId: string, schemaType: ExtractionRecord["schemaType"]) =>
    request<ExtractionDetail>(`/extractions/documents/${documentId}`, {
      method: "POST",
      body: JSON.stringify({ schemaType })
    }),
  extraction: (extractionId: string) => request<ExtractionDetail>(`/extractions/${extractionId}`),
  retryExtraction: (extractionId: string) => request<ExtractionDetail>(`/extractions/${extractionId}/retry`, {
    method: "POST",
    body: JSON.stringify({})
  }),
  reviewExtractionField: (extractionId: string, fieldId: string, input: {
    version: number;
    decision: "accepted" | "corrected" | "rejected";
    correctedValue?: string | number | null;
  }) => request<{ reviewed: boolean; version: number }>(`/extractions/${extractionId}/fields/${fieldId}`, {
    method: "PATCH",
    body: JSON.stringify(input)
  }),
  confirmExtraction: (extractionId: string) => request<{ confirmed: boolean }>(`/extractions/${extractionId}/confirm`, {
    method: "POST",
    body: JSON.stringify({ acknowledgeHumanReview: true })
  }),
  transferExtractionField: (extractionId: string, input: {
    personalTaxCaseId: string;
    caseVersion: number;
    fieldId: string;
    target:
      | { kind: "income"; category: "employment" | "business" | "professional" | "investment" | "rental" | "other"; code: string; label: string }
      | { kind: "exempt_income" | "deduction"; code: string; label: string }
      | { kind: "tax_credit"; creditType: "paye" | "withholding_tax" | "advance_payment" | "other"; code: string; label: string };
  }) => request<{ transferred: boolean; caseVersion: number }>(`/extractions/${extractionId}/transfers`, {
    method: "POST",
    body: JSON.stringify(input)
  }),
  taxAuthorities: () => request<{ authorities: TaxAuthority[] }>("/filing/authorities"),
  filingRecords: () => request<{ records: FilingRecord[] }>("/filing/records"),
  recordFiling: (input: {
    personalTaxCaseId: string;
    authorityCode: "LIRS" | "FCTIRS";
    status: "submitted_by_user" | "paid_by_user";
    externalReference: string | null;
    proofDocumentId: string | null;
  }) => request<{ recorded: boolean; recordId: string; verificationStatus: string }>("/filing/records", {
    method: "POST",
    body: JSON.stringify({ ...input, acknowledgeUserRecordedOnly: true })
  }),
  billingPlans: () => request<{ configured: boolean; mode: "disabled" | "test" | "live"; plans: BillingPlan[] }>("/billing/plans"),
  billingSummary: (businessId: string) => request<BillingSummary>(`/billing/businesses/${businessId}/summary`),
  createBillingCheckout: (businessId: string, planId: BillingPlan["id"]) => request<{
    checkoutId: string;
    reference: string;
    authorizationUrl: string;
    plan: { id: string; name: string };
    pricing: { baseAmountKobo: number; vatAmountKobo: number; totalAmountKobo: number; currency: string };
  }>(`/billing/businesses/${businessId}/checkout`, {
    method: "POST",
    headers: { "Idempotency-Key": crypto.randomUUID() },
    body: JSON.stringify({ planId })
  }),
  verifyBilling: (reference: string) => request<{ verified: boolean; reference: string; businessId: string; planId: string }>(`/billing/verify/${encodeURIComponent(reference)}`),
  billingManagementLink: (businessId: string) => request<{ managementUrl: string }>(`/billing/businesses/${businessId}/manage`, {
    method: "POST",
    body: JSON.stringify({})
  }),
  reportUrl: (businessId: string, reportId: string, format: "csv" | "pdf") => `/api/v1/payroll/businesses/${businessId}/reports/${reportId}/${format}`
};
